import type { RepoScore, ScoreHistory, GapAnalysis, ScanStatus, AssessmentResult, SledHierarchy, TeamRepoScore, RepoExecution, IterationMetrics, DiscoveryFeatureListView, DiscoveryFeatureDetailView, DiscoveryTeamAggregate, CalibrationRecord, DiscoverySummary, DiscoveryGapEntry, DiscoveryScanStatusResponse, RollupResponse } from '@sdd-maturity/shared-types';
import { mockApiClient } from './fixtures/mock-api-client';
import { rollupResponseToRollupData } from './rollup';
import type { RollupData } from './rollup';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api';
const USE_MOCK = import.meta.env.VITE_USE_MOCK_API === 'true';

interface RequestOptions {
  method?: string;
  body?: unknown;
  token?: string;
}

/** Thrown when the API responds with 401/403 — useApi catches this to render the "Please sign in" state. */
export class UnauthorizedError extends Error {
  constructor(message = 'Session expired — please sign in again') {
    super(message);
    this.name = 'UnauthorizedError';
  }
}

/**
 * Thrown for non-2xx responses (other than 401/403). Carries the HTTP status
 * and the response body's `code` field so callers can distinguish specific
 * conditions — e.g. the Discovery scan 409 `SCAN_RECENTLY_COMPLETED` gate —
 * from a generic failure. Subclasses Error so existing `.message` callers work.
 */
export class ApiError extends Error {
  status: number;
  code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

/**
 * Scan-status polling is keyed by either an SFN `executionArn` (always of the
 * form `arn:aws:states:...`) or, before the execution exists, the opaque
 * `requestId` a queued scan carries (AC-5) — route each to the query param
 * the backend status handler expects.
 */
function scanStatusQueryParam(key: string): string {
  return key.startsWith('arn:')
    ? `executionArn=${encodeURIComponent(key)}`
    : `requestId=${encodeURIComponent(key)}`;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, token } = options;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers,
    ...(body ? { body: JSON.stringify(body) } : {}),
  });

  if (response.status === 401 || response.status === 403) {
    // Session expired or token invalid — clear stored token so useAuth re-prompts.
    sessionStorage.removeItem('auth_id_token');
    throw new UnauthorizedError();
  }

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({ error: { message: response.statusText } }));
    const message = errorBody.error?.message ?? errorBody.message ?? `API error: ${response.status}`;
    throw new ApiError(message, response.status, errorBody.code);
  }

  return response.json() as Promise<T>;
}

/**
 * Builds the `?org=…[&repo=…]` query string the org/repo read routes now use
 * INSTEAD of carrying those values as URI path segments.
 *
 * Why: AWS WAF's `AWSManagedRulesAdminProtectionRuleSet` blocks any request
 * whose URI *path* contains `admin` (its `AdminProtection_URIPATH` rule, aimed
 * at exposed admin consoles). An org legitimately named `PricingAdminTools`
 * therefore made `/orgs/PRICINGADMINTOOLS/repos` return a WAF `403` before the
 * request ever reached the authorizer or the Lambda — surfacing in the browser
 * as an opaque `Failed to fetch` that looks exactly like a CORS error (the CORS
 * headers were in fact present and correct all along). The WAF rule inspects
 * only the path, not the query string, so moving the values here sidesteps the
 * false positive without weakening the WAF for real admin paths.
 *
 * `URLSearchParams` handles the escaping, so callers must NOT pre-encode.
 */
function orgRepoQuery(org: string, repo?: string): string {
  const params = new URLSearchParams({ org });
  if (repo !== undefined) params.set('repo', repo);
  return params.toString();
}

type GetAllTeamsResponse = { teams: Array<{ number: string; name: string; tier: number; tierLabel: string; level: string; active: boolean }> };

/**
 * Module-level single-flight cache for `getAllTeams()` (TASK-2 — FR-_ → AC-2).
 * `SearchLauncher.tsx` and `useScopeNode.ts` each mount independently and both
 * want "the cached `getAllTeams()` tree" — without this, every mount issued its
 * own `/sled/hierarchy?all=true` fetch. Concurrent callers share the one
 * in-flight promise; on rejection the cache is cleared so the next caller
 * retries against the network instead of replaying the same failure until a
 * full page reload; on success the resolved value stays cached for the
 * module's lifetime (the page session).
 */
let allTeamsCache: Promise<GetAllTeamsResponse> | null = null;

function getAllTeamsCached(token?: string): Promise<GetAllTeamsResponse> {
  if (!allTeamsCache) {
    allTeamsCache = request<GetAllTeamsResponse>(`/sled/hierarchy?all=true`, { token }).catch((err) => {
      allTeamsCache = null;
      throw err;
    });
  }
  return allTeamsCache;
}

export const realApiClient = {
  getOrgRepos(org: string, token?: string): Promise<RepoScore[]> {
    return request<RepoScore[]>(`/org-repos?${orgRepoQuery(org)}`, { token });
  },

  getScoreHistory(org: string, repo: string, token?: string): Promise<ScoreHistory[]> {
    return request<ScoreHistory[]>(
      `/repo-scores?${orgRepoQuery(org, repo)}`,
      { token }
    );
  },

  getReport(org: string, repo: string, token?: string): Promise<{ url: string; scanTimestamp: string }> {
    return request<{ url: string; scanTimestamp: string }>(
      `/repo-report?${orgRepoQuery(org, repo)}`,
      { token }
    );
  },

  getGaps(token?: string): Promise<GapAnalysis[]> {
    return request<GapAnalysis[]>('/gaps', { token });
  },

  triggerScan(
    input: { type: 'repo' | 'org' | 'team'; orgName?: string; repoName?: string; teamNumber?: string },
    token?: string
  ): Promise<{ executionArn?: string; requestId?: string; status: string; startDate?: string; repoCount?: number; teamNumber?: string; repoExecutions?: RepoExecution[] }> {
    return request('/scan', { method: 'POST', body: input, token });
  },

  getReportData(org: string, repo: string, token?: string): Promise<AssessmentResult> {
    return request<AssessmentResult>(
      `/repo-report-data?${orgRepoQuery(org, repo)}`,
      { token }
    );
  },

  getScanStatus(key: string, token?: string): Promise<ScanStatus> {
    return request<ScanStatus>(`/scan/status?${scanStatusQueryParam(key)}`, { token });
  },

  getTeamHierarchy(token?: string, parentTeamNumber?: string): Promise<SledHierarchy> {
    const query = parentTeamNumber ? `?parent=${encodeURIComponent(parentTeamNumber)}` : '';
    return request<SledHierarchy>(`/sled/hierarchy${query}`, { token });
  },

  getAllTeams(token?: string): Promise<GetAllTeamsResponse> {
    return getAllTeamsCached(token);
  },

  getTeamRepos(teamNumber: string, token?: string): Promise<{ repos: TeamRepoScore[] }> {
    return request<{ repos: TeamRepoScore[] }>(
      `/sled/teams/${encodeURIComponent(teamNumber)}/repos`,
      { token }
    );
  },

  getGapsFiltered(teamNumber?: string, token?: string): Promise<GapAnalysis[]> {
    const query = teamNumber ? `?teamNumber=${encodeURIComponent(teamNumber)}` : '';
    return request<GapAnalysis[]>(`/gaps${query}`, { token });
  },

  /**
   * Rich multi-team rollup for any non-leaf node (Portfolio/Stream/Train), served
   * by the precomputed `GET /rollup/{n}?mode=` endpoint (TASK-6 — FR-1 → AC-1.1).
   * Fetches the Delivery and Discovery views IN PARALLEL and reshapes them with the
   * pure `rollupResponseToRollupData` adapter, so the #629 components render unchanged.
   * Replaces the old client-side team-endpoint fan-out (getAllTeams + per-team
   * getTeamRepos/getDiscoveryScores). Both fetches carry the Cognito token (CC-13).
   */
  async getRollup(nodeNumber: string, token?: string): Promise<RollupData> {
    const path = `/rollup/${encodeURIComponent(nodeNumber)}`;
    const [delivery, discovery] = await Promise.all([
      request<RollupResponse>(`${path}?mode=delivery`, { token }),
      request<RollupResponse>(`${path}?mode=discovery`, { token }),
    ]);
    return rollupResponseToRollupData(delivery, discovery);
  },

  getTeamIterationMetrics(teamNumber: string, token?: string): Promise<{ metrics: IterationMetrics | null }> {
    return request<{ metrics: IterationMetrics | null }>(
      `/teams/${encodeURIComponent(teamNumber)}/iteration-metrics`,
      { token },
    );
  },

  // ── Discovery Quality Scoring API methods (TASK-7, UI-04) ──

  getDiscoverySummary(teamNumber: string, token?: string): Promise<DiscoverySummary> {
    return request<DiscoverySummary>(
      `/teams/${encodeURIComponent(teamNumber)}/discovery`,
      { token }
    );
  },

  getDiscoveryScores(teamNumber: string, token?: string): Promise<DiscoveryFeatureListView[]> {
    return request<DiscoveryFeatureListView[]>(
      `/teams/${encodeURIComponent(teamNumber)}/discovery/scores`,
      { token }
    );
  },

  getDiscoveryFeature(formattedId: string, token?: string, teamNumber?: string): Promise<DiscoveryFeatureDetailView> {
    if (teamNumber) {
      return request<DiscoveryFeatureDetailView>(
        `/teams/${encodeURIComponent(teamNumber)}/discovery/features/${encodeURIComponent(formattedId)}`,
        { token }
      );
    }
    // Fallback: try without teamNumber (will 400 from backend — callers must provide teamNumber)
    return request<DiscoveryFeatureDetailView>(
      `/teams/unknown/discovery/features/${encodeURIComponent(formattedId)}`,
      { token }
    );
  },

  getDiscoveryFeatureHistory(formattedId: string, teamNumber: string, token?: string): Promise<DiscoveryFeatureDetailView[]> {
    return request<DiscoveryFeatureDetailView[]>(
      `/teams/${encodeURIComponent(teamNumber)}/discovery/features/${encodeURIComponent(formattedId)}/history`,
      { token }
    );
  },

  triggerDiscoveryScan(
    teamNumber: string,
    token?: string,
    teamName?: string,
    formattedIds?: string[],
  ): Promise<{ executionArn?: string; requestId?: string; status?: string; startDate?: string }> {
    return request('/scan/discovery', { method: 'POST', body: { teamNumber, teamName, formattedIds }, token });
  },

  getDiscoveryScanStatus(key: string, token?: string): Promise<DiscoveryScanStatusResponse> {
    return request<DiscoveryScanStatusResponse>(
      `/scan/discovery/status?${scanStatusQueryParam(key)}`,
      { token }
    );
  },

  getDiscoveryGaps(teamNumber?: string, token?: string): Promise<GapAnalysis[]> {
    const query = teamNumber ? `?teamNumber=${encodeURIComponent(teamNumber)}` : '';
    return request<GapAnalysis[]>(`/gaps/discovery${query}`, { token });
  },

  getCalibrations(token?: string): Promise<CalibrationRecord[]> {
    return request<CalibrationRecord[]>('/calibration', { token });
  },

  submitCalibration(record: Partial<CalibrationRecord>, token?: string): Promise<CalibrationRecord> {
    return request<CalibrationRecord>('/calibration', { method: 'POST', body: record, token });
  },
};

export const apiClient: typeof realApiClient = USE_MOCK
  ? (mockApiClient as typeof realApiClient)
  : realApiClient;

if (USE_MOCK && typeof window !== 'undefined') {
  console.info(
    '%c[mock-api] active — using mock-api-client.ts (set VITE_USE_MOCK_API=false to disable)',
    'color:#888;font-weight:bold',
  );
}
